"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  Badge,
  VStack,
  HStack,
  Textarea,
  Spinner,
  Center,
  Image,
  Link as ChakraLink,
  Icon,
  useDisclosure,
} from "@chakra-ui/react";
import { FiArrowLeft, FiDownload, FiStar } from "react-icons/fi";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import type { Document } from "../types/types";
import FilenameDialog from "./FilenameDialog";

export default function DocumentDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [docData, setDocData] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState<number>(0);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [downloadData, setDownloadData] = useState<{
    blob: Blob;
    defaultFilename: string;
  } | null>(null);

  useEffect(() => {
    async function fetchDocument() {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          throw new Error("User not found");
        }

        const { data, error: documentError } = await supabase
          .from("documents")
          .select(
            `
            *,
            document_type:document_type_id(name, description)
          `
          )
          .eq("id", id)
          .eq("user_id", user.id)
          .single();

        if (documentError) {
          throw documentError;
        }

        setDocData(data);

        if (data.feedback) {
          setFeedback(data.feedback);
        }
        if (data.feedback_rating) {
          setRating(data.feedback_rating);
        }
      } catch (error: any) {
        console.error("Error fetching document:", error.message);
        setError("Failed to load document details. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    fetchDocument();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PROCESSING":
        return "yellow";
      case "COMPLETED":
        return "green";
      case "FAILED":
        return "red";
      default:
        return "gray";
    }
  };

  const getDocumentDisplayName = (doc: Document) => {
    // For Miscellaneous documents, use custom_document_name if available
    if (
      doc.document_type?.name === "Miscellaneous" &&
      doc.custom_document_name
    ) {
      return doc.custom_document_name;
    }
    return doc.document_type?.name || "Document";
  };

  const downloadDocument = async () => {
    if (!docData || !docData.output_path) return;

    try {
      const { data, error } = await supabase.storage
        .from("document-outputs")
        .download(docData.output_path);

      if (error) {
        throw error;
      }

      const defaultFilename = `${getDocumentDisplayName(docData)}-${
        docData.id
      }.${docData.output_format.toLowerCase()}`;

      setDownloadData({
        blob: data,
        defaultFilename,
      });
      onOpen();
    } catch (error: any) {
      console.error("Error downloading document:", error.message);
      setError("Failed to download document. Please try again.");
    }
  };

  const submitFeedback = async () => {
    if (!docData || !feedback.trim()) return;

    try {
      setSubmittingFeedback(true);

      const { error } = await supabase
        .from("documents")
        .update({
          feedback: feedback.trim(),
          feedback_rating: rating,
          updated_at: new Date().toISOString(),
        })
        .eq("id", docData.id);

      if (error) {
        throw error;
      }

      setDocData({
        ...docData,
        feedback: feedback.trim(),
        feedback_rating: rating,
      });

      alert("Feedback submitted successfully!");
    } catch (error: any) {
      console.error("Error submitting feedback:", error.message);
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleConfirmDownload = (filename: string) => {
    if (!downloadData) return;

    const url = URL.createObjectURL(downloadData.blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = filename;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Clean up
    setDownloadData(null);
  };

  if (loading) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="primary.500" />
          <Text>Loading document details...</Text>
        </VStack>
      </Center>
    );
  }

  if (error || !docData) {
    return (
      <Center minH="100vh">
        <VStack spacing={4} textAlign="center">
          <Heading size="lg" color="red.500">
            Error
          </Heading>
          <Text color="gray.600">{error || "Document not found"}</Text>
          <Button
            onClick={() => navigate("/document-history")}
            colorScheme="primary"
          >
            Back to Documents
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box
        bg="rgb(253, 251, 252)"
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container maxW="7xl">
          <Flex h={16} align="center" justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI"
                height="50px"
                width="auto"
                objectFit="contain"
              />
            </HStack>

            <HStack spacing={6}>
              <ChakraLink
                as={Link}
                to="/dashboard"
                color="gray.600"
                fontWeight="medium"
              >
                Dashboard
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/document-history"
                color="gray.600"
                fontWeight="medium"
              >
                Documents
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/templates"
                color="gray.600"
                fontWeight="medium"
              >
                Templates
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/billing"
                color="gray.600"
                fontWeight="medium"
              >
                Billing
              </ChakraLink>
            </HStack>

            <Button
              onClick={() => navigate("/document-history")}
              variant="ghost"
              leftIcon={<Icon as={FiArrowLeft} />}
            >
              Back
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="7xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Document Header */}
          <Card>
            <CardBody>
              <Flex justify="space-between" align="flex-start">
                <VStack align="start" spacing={3}>
                  <Heading size="xl" color="gray.800">
                    {getDocumentDisplayName(docData)}
                  </Heading>
                  <Badge colorScheme={getStatusColor(docData.status)} size="lg">
                    {docData.status.replace("_", " ")}
                  </Badge>
                </VStack>

                {docData.status === "COMPLETED" && docData.output_path && (
                  <Button
                    onClick={downloadDocument}
                    colorScheme="green"
                    leftIcon={<Icon as={FiDownload} />}
                  >
                    Download {docData.output_format}
                  </Button>
                )}
              </Flex>
            </CardBody>
          </Card>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {/* Document Information */}
            <Card>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="gray.800">
                    Document Information
                  </Heading>

                  <VStack spacing={3} align="stretch">
                    <Flex justify="space-between">
                      <Text fontWeight="medium" color="gray.600">
                        Document ID:
                      </Text>
                      <Text color="gray.800">{docData.id}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontWeight="medium" color="gray.600">
                        Type:
                      </Text>
                      <Text color="gray.800">
                        {getDocumentDisplayName(docData)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontWeight="medium" color="gray.600">
                        Created:
                      </Text>
                      <Text color="gray.800">
                        {formatDate(docData.created_at)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontWeight="medium" color="gray.600">
                        Updated:
                      </Text>
                      <Text color="gray.800">
                        {formatDate(docData.updated_at)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontWeight="medium" color="gray.600">
                        Output Format:
                      </Text>
                      <Text color="gray.800">{docData.output_format}</Text>
                    </Flex>
                    {docData.delivered_at && (
                      <Flex justify="space-between">
                        <Text fontWeight="medium" color="gray.600">
                          Delivered:
                        </Text>
                        <Text color="gray.800">
                          {formatDate(docData.delivered_at)}
                        </Text>
                      </Flex>
                    )}
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Prompt & Instructions */}
            <Card>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="gray.800">
                    Prompt & Instructions
                  </Heading>
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                    whiteSpace="pre-wrap"
                    fontSize="sm"
                    lineHeight="1.6"
                    color="gray.800"
                  >
                    {docData.prompt}
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Feedback & Rating */}
          <Card>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Heading size="md" color="gray.800">
                  Feedback & Rating
                </Heading>

                <VStack spacing={4} align="stretch">
                  <VStack spacing={2} align="start">
                    <Text fontWeight="medium" color="gray.700">
                      Rate this document:
                    </Text>
                    <HStack spacing={1}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Button
                          key={star}
                          variant="ghost"
                          size="sm"
                          p={1}
                          minW="auto"
                          h="auto"
                          onClick={() => setRating(star)}
                          isDisabled={submittingFeedback}
                          color={star <= rating ? "yellow.400" : "gray.300"}
                          _hover={{
                            color: star <= rating ? "yellow.500" : "yellow.200",
                          }}
                        >
                          <Icon as={FiStar} boxSize={6} fill="currentColor" />
                        </Button>
                      ))}
                    </HStack>
                  </VStack>

                  <VStack spacing={2} align="start">
                    <Text fontWeight="medium" color="gray.700">
                      Your feedback:
                    </Text>
                    <Textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Share your thoughts about this document..."
                      rows={4}
                      isDisabled={submittingFeedback}
                      bg="gray.50"
                    />
                  </VStack>

                  <Button
                    onClick={submitFeedback}
                    isDisabled={!feedback.trim() || submittingFeedback}
                    colorScheme="primary"
                    isLoading={submittingFeedback}
                    loadingText="Submitting..."
                    alignSelf="flex-start"
                  >
                    Submit Feedback
                  </Button>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
      {/* Filename Dialog */}
      <FilenameDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDownload}
        defaultFilename={downloadData?.defaultFilename || ""}
        title="Download Document"
      />
    </Box>
  );
}
